import type React from "react";
import {Skeleton, Stack, Typography} from "@mui/material";
import {CustomCard} from "~/components/Card/Card";
import {useSupplierFirmographicsQuery} from "~/pages/supplier-360/hooks/useSupplierFirmographicsQuery";
import {useSupplierExtLinkQuery} from "~/pages/supplier-360/hooks/useSupplierExtLinkQuery";
import {MatchReviewBanner} from "~/pages/supplier-360/components/pdl/MatchReviewBanner";
import {SnapshotMeta} from "~/pages/supplier-360/components/pdl/SnapshotMeta";
import {formatCount, formatUsdRange, hasReviewNotice, titleCase} from "~/pages/supplier-360/components/pdl/pdlFormat";

function FactRow({label, value}: {label: string, value: React.ReactNode}) {
    return (
        <Stack direction="row" alignItems="baseline" gap={1} sx={{py: 0.5}}>
            <Typography variant="caption" color="grey.700" sx={{width: 90, flexShrink: 0}}>{label}</Typography>
            <Typography variant="body2">{value}</Typography>
        </Stack>
    );
}

/**
 * Overview "Company facts": employees, type and revenue from the entity's latest firmographics
 * snapshot (D&B or company-data enrichment — whichever last refreshed the profile; there's no
 * server-side per-field precedence, so this card shows one snapshot's own values, not a merge).
 */
export const CompanyFactsCard: React.FC<{entityId: number}> = ({entityId}) => {
    const {data: firmographics, isLoading} = useSupplierFirmographicsQuery(entityId);
    const {data: link, isLoading: linkLoading} = useSupplierExtLinkQuery(entityId);

    const rows = firmographics ? [
        firmographics.employee_count !== null
            ? {label: 'Employees', value: formatCount(firmographics.employee_count)} : null,
        firmographics.business_entity_type
            ? {label: 'Type', value: titleCase(firmographics.business_entity_type)} : null,
        firmographics.annual_revenue_usd !== null
            ? {label: 'Revenue', value: formatUsdRange(firmographics.annual_revenue_min_usd ?? firmographics.annual_revenue_usd,
                firmographics.annual_revenue_max_usd)} : null,
    ].filter((row): row is {label: string, value: string} => row !== null) : [];

    // Both queries must have settled before deciding there's nothing to show — judging `link` while
    // it's still in flight (hasReviewNotice(undefined) === false) hides the card, then remounts it the
    // moment that second request resolves.
    if (!isLoading && !linkLoading && rows.length === 0 && !hasReviewNotice(link)) return null;

    return (
        <CustomCard title="Company facts" sx={{flex: 1, minWidth: 0}}>
            {isLoading && <Skeleton variant="rectangular" height={120}/>}
            {!isLoading && (
                <Stack gap={1}>
                    <MatchReviewBanner link={link}/>
                    {rows.map(row => <FactRow key={row.label} label={row.label} value={row.value}/>)}
                    {firmographics && <SnapshotMeta retrievedAt={firmographics.retrieved_at}/>}
                </Stack>
            )}
        </CustomCard>
    );
};
